import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import type { Locale } from '@musicville/shared';
import { Icon } from '@/components/brand/Icon';
import { Logo } from '@/components/brand/Logo';
import { Button, LinkButton } from '@/components/ui/Button';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { LOCALE_LABELS, useI18n } from '@/lib/i18n';

const NAV = [
  { to: '/lessons', key: 'nav.lessons' },
  { to: '/teachers', key: 'nav.teachers' },
  { to: '/practice-rooms', key: 'nav.rooms' },
  { to: '/community', key: 'nav.community' },
  { to: '/about', key: 'nav.about' },
  { to: '/contact', key: 'nav.contact' },
];

export function Header({
  theme = 'midnight',
}: {
  theme?: 'midnight' | 'ivory' | undefined;
}): React.JSX.Element {
  const { t } = useI18n();
  const { user, logOut } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPath, setMenuPath] = useState(location.pathname);

  const onMidnight = theme === 'midnight';

  if (menuPath !== location.pathname) {
    setMenuPath(location.pathname);
    setMenuOpen(false);
  }

  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  return (
    <header
      className={cn(
        'relative z-40 border-b',
        onMidnight
          ? 'border-border-on-midnight bg-midnight text-on-midnight'
          : 'border-border-subtle bg-canvas text-primary',
      )}
    >
      <div className="mv-container flex h-22 items-center justify-between gap-4 2xl:gap-6">
        <Link to="/" aria-label="MusicVille Academy — home" className="shrink-0">
          <Logo
            lockup="horizontal"
            className={cn('h-9', onMidnight ? 'text-on-midnight' : 'text-primary')}
          />
        </Link>

        <nav aria-label="Main" className="hidden items-center gap-5 xl:flex 2xl:gap-8">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'whitespace-nowrap text-body-sm font-medium transition-opacity hover:opacity-70',
                  isActive && 'underline decoration-2 underline-offset-8',
                )
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <LocaleSwitcher onMidnight={onMidnight} />

          {user ? (
            <div className="hidden items-center gap-4 xl:flex">
              <Link to="/portal" className="text-body-sm font-medium hover:opacity-70">
                {user.fullName.split(' ')[0]}
              </Link>
              <button
                type="button"
                onClick={() => void logOut()}
                className="text-body-sm font-medium opacity-70 hover:opacity-100"
              >
                <Icon name="log-out" size={18} label="Log out" />
              </button>
            </div>
          ) : (
            <Link
              to="/auth/log-in"
              className="hidden whitespace-nowrap text-body-sm font-medium hover:opacity-70 xl:block"
            >
              {t('nav.logIn')}
            </Link>
          )}

          <LinkButton
            to="/booking/subject"
            size="md"
            className="hidden whitespace-nowrap sm:inline-flex"
          >
            {t('nav.bookTrial')}
          </LinkButton>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? t('nav.close') : t('nav.menu')}
            className="flex size-11 items-center justify-center xl:hidden"
          >
            <Icon name={menuOpen ? 'x' : 'menu'} size={24} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          id="mobile-nav"
          className={cn(
            'absolute inset-x-0 top-full border-b shadow-overlay xl:hidden',
            onMidnight ? 'border-border-on-midnight bg-midnight' : 'border-border-subtle bg-canvas',
          )}
        >
          <nav aria-label="Main" className="mv-container flex flex-col py-4">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn('py-3 text-body font-medium', isActive && 'text-accent')
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <div className="mt-4 flex flex-col gap-3 border-t border-current/10 pt-4">
              {user ? (
                <>
                  <Link to="/portal" className="py-2 text-body font-medium">
                    {user.fullName}
                  </Link>
                  <Button variant="secondary" onMidnight={onMidnight} onClick={() => void logOut()}>
                    Log out
                  </Button>
                </>
              ) : (
                <LinkButton to="/auth/log-in" variant="secondary" onMidnight={onMidnight} fullWidth>
                  {t('nav.logIn')}
                </LinkButton>
              )}
              <LinkButton to="/booking/subject" fullWidth>
                {t('nav.bookTrial')}
              </LinkButton>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}

function LocaleSwitcher({ onMidnight }: { onMidnight: boolean }): React.JSX.Element {
  const { locale, setLocale, locales } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Language: ${LOCALE_LABELS[locale]}`}
        className="flex items-center gap-1.5 rounded-sm px-1 py-1.5 text-label font-semibold hover:opacity-70"
      >
        <Icon name="globe" size={18} />
        {LOCALE_LABELS[locale]}
        <Icon name="chevron-down" size={14} />
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute right-0 top-full z-50 mt-2 min-w-28 overflow-hidden rounded-md border shadow-overlay',
            onMidnight
              ? 'border-border-on-midnight bg-midnight-raised'
              : 'border-border-subtle bg-surface',
          )}
        >
          {locales.map((option: Locale) => (
            <button
              key={option}
              type="button"
              role="menuitemradio"
              aria-checked={option === locale}
              onClick={() => {
                setLocale(option);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-body-sm',
                onMidnight ? 'hover:bg-midnight' : 'hover:bg-subtle',
                option === locale && 'font-semibold',
              )}
            >
              {LOCALE_LABELS[option]}
              {option === locale && <Icon name="check" size={16} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
